import { Head, Link, router } from "@inertiajs/react"
import {
  Box, Button, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography
} from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"
import AudioPlayButton from "../../../components/AudioPlayButton"
import TranslationsCell from "../../../components/TranslationsCell"

type Translation = { id: number; language: string; text: string; hint: string | null; position: number }

type Audio = { url: string; language: string; ai_model: string; voice: string }

type Sentence = {
  id: number
  kanji: string | null
  kana: string | null
  romaji: string | null
  translations: Translation[]
  audio: Audio | null
}

export default function AdminSentencesIndex({ sentences }: { sentences: Sentence[] }) {
  const remove = (s: Sentence) => {
    if (confirm("Delete this sentence?")) router.delete(`/admin/sentences/${s.id}`)
  }

  return (
    <AdminLayout title="Sentences" maxWidth={false}>
      <Head title="Admin · Sentences" />
      <Stack direction="row" sx={{ justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Sentences</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" component={Link} href="/admin/ai/sentence/new" data-testid="new-sentence-ai">
            New with AI
          </Button>
          <Button variant="contained" component={Link} href="/admin/sentences/new" data-testid="new-sentence">
            New sentence
          </Button>
        </Stack>
      </Stack>
      {sentences.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }} data-testid="sentences-empty">
          <Typography>No sentences yet.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table data-testid="sentences-table">
            <TableHead>
              <TableRow>
                <TableCell>Kanji</TableCell>
                <TableCell>Kana / Romaji</TableCell>
                <TableCell>Translations</TableCell>
                <TableCell>Audio</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sentences.map((s) => (
                <TableRow key={s.id} data-testid={`sentence-row-${s.id}`}>
                  <TableCell><Link href={`/admin/sentences/${s.id}`}>{s.kanji || "—"}</Link></TableCell>
                  <TableCell>
                    {s.kana || "—"}
                    {s.romaji && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {s.romaji}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><TranslationsCell translations={s.translations} /></TableCell>
                  <TableCell>
                    {s.audio
                      ? <AudioPlayButton src={s.audio.url} testId={`sentence-audio-${s.id}`} />
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                      <Button size="small" component={Link} href={`/admin/sentences/${s.id}/edit`}>Edit</Button>
                      <Button size="small" color="error" onClick={() => remove(s)}
                              data-testid={`delete-${s.id}`}>Delete</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AdminLayout>
  )
}
